import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { campaignsControllerCancelCampaign } from '@/generated/korza-api-client';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { getApiErrorMessage } from '@/lib/api-error';

export type CancelCampaignOutcome = 'cancelled' | 'alreadySent' | 'notFound';

export interface CancelCampaignVariables {
  campaignId: string;
  campaignName: string;
}

/**
 * Cancels (hard-deletes) a campaign. Decides by `response.status` (AD-025):
 * 2xx → 'cancelled', 409 → 'alreadySent', 404 → 'notFound', each with its
 * toast and a refresh of the list and the detail (the list keeps its URL
 * filter). Any other status rejects with the body's `message`.
 */
export function useCancelCampaign(): UseMutationResult<
  CancelCampaignOutcome,
  Error,
  CancelCampaignVariables
> {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  const { LL } = useI18nContext();

  return useMutation<CancelCampaignOutcome, Error, CancelCampaignVariables>({
    mutationFn: async ({ campaignId }) => {
      const result = await campaignsControllerCancelCampaign({
        path: { tenantSlug: tenant.slug, campaignId },
      });
      const { status } = result.response;
      if (status >= 200 && status < 300) return 'cancelled';
      if (status === 409) return 'alreadySent';
      if (status === 404) return 'notFound';
      throw new Error(
        getApiErrorMessage(
          result.error,
          LL.campaigns.cancelOutcome.genericError()
        )
      );
    },
    onSuccess: (outcome, { campaignId, campaignName }) => {
      if (outcome === 'cancelled') {
        toast.success(LL.campaigns.cancelOutcome.cancelled(), {
          description: LL.campaigns.campaignCancelledDescription({
            campaignName,
          }),
        });
      } else if (outcome === 'alreadySent') {
        toast.error(LL.campaigns.cancelOutcome.alreadySent());
      } else {
        toast.error(LL.campaigns.cancelOutcome.notFound());
      }
      // Not awaited: a deleted campaign's detail refetch fails (with retries)
      // and must not hold the caller's navigation back.
      void queryClient.invalidateQueries({
        queryKey: ['campaigns', tenant.slug],
      });
      void queryClient.invalidateQueries({
        queryKey: ['campaign', campaignId],
      });
    },
    onError: (error) => {
      toast.error(LL.campaigns.failedToCancelCampaign(), {
        description: error.message,
      });
    },
  });
}
